import { fetchCatalogAdIfNeeded } from '@/redux/modules/catalog/ad/catalogAd.actions';
import { CatalogAds } from '@/redux/modules/catalog/ad/catalogAd.types';
import { fetchCategoryRelatedSearches } from '@/redux/modules/categoryRelatedSearches';
import { loadChairishSimilarItems } from '@/redux/modules/chairishSimilarItems/chairishSimilarItems.actions';
import { fetchItemSummariesIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import { getSearchPageSize } from '@/redux/modules/pagination';
import { fetchRelatedSearchesForSearchPage } from '@/redux/modules/search/relatedSearches/relatedSearches.actions';
import { setIsZeroResults, submitSearch } from '@/redux/modules/search/search.actions';
import { getSearchedItemIds, getSearchUiState } from '@/redux/modules/search/search.selectors';
import { getSearchFacets } from '@/redux/modules/searchFacetAggregates.selectors';
import { getSEOSearchPageBreadcrumbs } from '@/redux/modules/searchPromoted.selectors';
import { fetchSecondarySearchSuggestions } from '@/redux/modules/searchSuggestions/searchSuggestions.actions';
import { getSecondarySearchSuggestions } from '@/redux/modules/searchSuggestions/searchSuggestions.selectors';
import { fetchSellersIfNeeded } from '@/redux/modules/seller';
import { fetchSimilarItemsGroups, getSimilarItemsGroupsItemIds } from '@/redux/modules/similarItemsGroups';
import { DataLoaderParams } from '@/types/DataLoaderParams';
import FacetTypes from '@/types/search/enums/facetTypes';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { SearchPageName } from '@/types/search/searchQuery';
import { FilterAction } from '@/types/SearchFacet';
import { AmplitudeExperiments, AmplitudeVariants } from '@/utils/amplitude/experiments/AmplitudeExperiments';
import {
    getUnresolvedSemanticBrowseValuesAsKeyword,
    resolveSemanticBrowseDimensionValuesInSearchQuery,
    searchQueryHasUnresolvedSemanticBrowseValues,
} from '@/utils/browsePath/browsePath';
import cleanSearchTerms from '@/utils/cleanSearchTerms';
import {
    getCategoryIdsFromQueryParams,
    getKeywordFromQueryParams,
    isArchivedSearch,
    parseQueryParams,
} from '@/utils/queryParams';
import { buildSearchQuery, isBuyNowQuery } from '@/utils/search/searchQuery';
import { parseSearchSeoPath, resolveSearchSeoPathSubjectToBrowseFacetIds } from '@/utils/search/searchSeoPath';
import { getIsCollectionsPage, getIsSEOSearchPage, getIsSEOSearchPageWithLocation } from '@/utils/urls';

export default ({ amplitudeExperimentFlags, location, routerState, store }: DataLoaderParams) => {
    const { dispatch, getState } = store;
    const queryParams = parseQueryParams(location.search);
    const { pathname } = location;

    const archivedSearch = isArchivedSearch(queryParams, pathname);

    // Experiment: SEAR-998 Popularity Score Re-Ranking
    const popularityScoreReRankingTreatment =
        amplitudeExperimentFlags[AmplitudeExperiments.PopularityScoreReRanking]?.value;

    let popularityScoreReRankingVersion: string | undefined;
    if (popularityScoreReRankingTreatment === AmplitudeVariants.PopularityScoreReRanking.AddPopularityKeepingBidCount) {
        popularityScoreReRankingVersion = 'v2';
    } else if (
        popularityScoreReRankingTreatment === AmplitudeVariants.PopularityScoreReRanking.AddPopularityRemovingBidCount
    ) {
        popularityScoreReRankingVersion = 'v3';
    }

    const nonFilterActions: FilterAction[] = [
        FilterAction.PageChanged,
        FilterAction.PageSizeChanged,
        FilterAction.SortByChanged,
        FilterAction.ViewPriceResults,
        FilterAction.ViewUpcomingItems,
    ];
    const isSSR = !routerState;
    const isFilterRefinement =
        Boolean(routerState?.filterAction) && !nonFilterActions.includes(routerState?.filterAction);
    const needsAggs = isSSR || isFilterRefinement;

    return (async () => {
        const currentState = getState();

        const pageSize = getSearchPageSize(currentState);
        let browseFacetOptionIds = resolveSearchSeoPathSubjectToBrowseFacetIds(pathname, getSearchFacets(currentState));
        const buildSearchQueryForPage = (qp: typeof queryParams) =>
            buildSearchQuery({
                browseFacetOptionIds,
                pathname,
                queryParams: qp,
                searchPageSize: pageSize,
            });

        let searchQuery = buildSearchQueryForPage(queryParams);

        // Detect cold-load slug filters that can't be resolved yet (facets empty before first search).
        // Reference equality check: if resolution returns the same object, no progress was made.
        const hasUnresolvedSemanticFilterAtColdLoad =
            searchQueryHasUnresolvedSemanticBrowseValues(searchQuery) &&
            resolveSemanticBrowseDimensionValuesInSearchQuery(searchQuery, getSearchFacets(currentState)) ===
                searchQuery;

        // Force full (non-skipAggs) facets for this case so the retry below has real
        // options to resolve the slug against -- skipAggs responses only synthesize a
        // minimal facet for whatever's already selected, which isn't what we need here.
        const skipAggs = !needsAggs && !hasUnresolvedSemanticFilterAtColdLoad;

        const isBIN = isBuyNowQuery(searchQuery);
        searchQuery.buyNow = !archivedSearch && isBIN;
        const { searchTerm } = searchQuery;

        // Narrow the throwaway first request with a fallback keyword derived from the
        // unresolved slug(s) (e.g. `material=oil` → "oil") when no keyword is already
        // present. Without this, an unscoped `/search` matches items across every category
        // and search-party never surfaces attribute facets like "Materials & Techniques"
        // in the aggregation -- so the slug could never resolve regardless of skipAggs.
        // Mirrors the `keywordFallbackForUnresolvedBrowse` pattern used by category browse
        // routes. The injected keyword is only used for the initial request; the retry
        // re-dispatches with the original `searchQuery` (no keyword) once the slug resolves.
        const fallbackKeyword =
            hasUnresolvedSemanticFilterAtColdLoad && !getKeywordFromQueryParams(queryParams)
                ? getUnresolvedSemanticBrowseValuesAsKeyword(searchQuery)
                : '';
        const initialSearchQuery = fallbackKeyword
            ? buildSearchQueryForPage({ ...queryParams, keyword: fallbackKeyword })
            : searchQuery;
        if (initialSearchQuery !== searchQuery) {
            initialSearchQuery.buyNow = searchQuery.buyNow;
        }

        let pageName = archivedSearch ? SearchPageName.SOLD_SEARCH : SearchPageName.SEARCH;

        const isSEOSearchPage = getIsSEOSearchPage(pathname) || getIsSEOSearchPageWithLocation(pathname);
        if (isSEOSearchPage) {
            pageName = SearchPageName.SEO_SEARCH;
        } else if (getIsCollectionsPage(pathname)) {
            pageName = SearchPageName.COLLECTIONS;
        }

        const keywords = getKeywordFromQueryParams(queryParams);

        const promises = [
            dispatch(
                submitSearch({
                    pageName,
                    popularityScoreReRankingVersion,
                    routerState,
                    searchQuery: initialSearchQuery,
                    skipAggs,
                })
            ),
            dispatch(
                fetchSecondarySearchSuggestions({
                    term: cleanSearchTerms(searchTerm),
                })
            ),
            // fetch the search results page banner ad - with force in order to fetch a new ad each time the page changes
            dispatch(
                fetchCatalogAdIfNeeded({
                    force: true,
                    pageName: CatalogAds.SearchPageBanner,
                })
            ),
        ];

        await Promise.all(promises);

        const subjectTail = parseSearchSeoPath(pathname)?.subjectValueSlug;
        if (subjectTail && !browseFacetOptionIds?.[FacetTypes.ART_SUBJECT_ID]?.length && !/^\d+$/.test(subjectTail)) {
            const retryBrowse = resolveSearchSeoPathSubjectToBrowseFacetIds(pathname, getSearchFacets(getState()));
            if (retryBrowse?.[FacetTypes.ART_SUBJECT_ID]?.length) {
                browseFacetOptionIds = retryBrowse;
                searchQuery = buildSearchQueryForPage(queryParams);
                await dispatch(
                    submitSearch({
                        pageName,
                        popularityScoreReRankingVersion,
                        routerState,
                        searchQuery,
                        skipAggs: !needsAggs,
                    })
                );
            }
        }

        // Retry for cold-load semantic filter params (material=oil, creator=..., etc.).
        // The first request above was forced to fetch full aggregations (skipAggs: false)
        // for exactly this case, so state.search.facets should now contain the option
        // needed to resolve the slug -- re-check resolution and only re-dispatch
        // submitSearch if it now succeeds.
        if (
            hasUnresolvedSemanticFilterAtColdLoad &&
            resolveSemanticBrowseDimensionValuesInSearchQuery(searchQuery, getSearchFacets(getState())) !== searchQuery
        ) {
            await dispatch(
                submitSearch({
                    pageName,
                    popularityScoreReRankingVersion,
                    routerState,
                    searchQuery,
                    skipAggs,
                })
            );
        }

        const searchState = getState();
        const itemIds = getSearchedItemIds(searchState);
        const { totalFound, totalSoldItems } = getSearchUiState(searchState);

        const shouldPreloadSoldItems = !archivedSearch && totalFound === 0 && totalSoldItems > 0;

        if (itemIds.length) {
            // If we have search results do all the stuff to get the data for them.
            const { houses } = getSecondarySearchSuggestions(searchState);

            const promises: Promise<any>[] = [
                dispatch(
                    fetchCatalogAdIfNeeded({
                        categoryIds: getCategoryIdsFromQueryParams(queryParams),
                        keywords: `${keywords}`,
                        pageName,
                    })
                ),
                // Fetch item summaries for search results to enable SSR
                dispatch(
                    fetchItemSummariesIfNeeded({
                        identifier: 'SEARCH-PAGE-RESULTS',
                        itemIds,
                    })
                ),
            ];

            if (houses?.[0]?.houseId) {
                promises.push(dispatch(fetchSellersIfNeeded([houses[0]?.houseId])));
            }

            if (isSEOSearchPage) {
                const searchPageBreadcrumbs = getSEOSearchPageBreadcrumbs(searchState);
                if (searchPageBreadcrumbs.length > 0) {
                    const relatedCategoryId = searchPageBreadcrumbs[searchPageBreadcrumbs.length - 1].id;
                    promises.push(dispatch(fetchCategoryRelatedSearches(itemIds, relatedCategoryId)));
                }
            }

            promises.push(dispatch(fetchRelatedSearchesForSearchPage(searchTerm)));

            return Promise.all(promises);
        } else {
            // If we get no results back, we'll get the data for our zero results page
            if (shouldPreloadSoldItems) {
                await dispatch(setIsZeroResults(true));
                await dispatch(
                    submitSearch({
                        pageName,
                        routerState,
                        searchQuery: buildSearchQuery({
                            browseFacetOptionIds,
                            defaultSortOrder: SearchSortAndDirection.TimeNewest,
                            pathname,
                            queryParams: {
                                ...queryParams,
                                status: 'archive',
                            },
                            searchPageSize: pageSize,
                            similarItems: true,
                        }),
                        skipAggs: true,
                    })
                );
            }

            const { soldItemIds = [] } = getSearchUiState(searchState);
            return dispatch(
                fetchItemSummariesIfNeeded({
                    identifier: 'SEARCH-PAGE-SOLD',
                    itemIds: soldItemIds,
                    similarItems: true,
                })
            ).then(() => {
                const state = getState();
                const searchState = getSearchUiState(state);

                if (searchState?.itemIds?.length > 0) {
                    const itemId = searchState.itemIds[0];

                    return Promise.all([
                        dispatch(fetchSimilarItemsGroups(itemId)),
                        dispatch(loadChairishSimilarItems({ itemId })),
                    ]).then(() => {
                        const state = getState();
                        const similarItemIds = getSimilarItemsGroupsItemIds(state, itemId);
                        return dispatch(
                            fetchItemSummariesIfNeeded({
                                identifier: 'SEARCH-PAGE-SIMILAR',
                                itemIds: similarItemIds,
                            })
                        );
                    });
                }
            });
        }
    })();
};
